import React, { useState } from 'react';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Button, ButtonText } from '@/components/gluestack/button';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { useQueryClient } from '@tanstack/react-query';
import { decodeHTML } from 'entities';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useTranslation } from 'react-i18next';
import { PhotosService } from '@/services/domain/PhotosService';
import { Photo } from '@/models/Photo';
import { plainToClass } from '@/models/transform';

export interface IEditPhotoCaptionProps extends IContextSheetContentProps {
  payload?: {
    photo: Photo;
  };
  onCaptionEdited?: (caption: string) => void;
}

export function EditPhotoCaption({ onClose, payload, onCaptionEdited }: IEditPhotoCaptionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const photo = payload?.photo;
  const [caption, setCaption] = useState<string>(decodeHTML(photo?.Caption ?? ''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useDelayedAutoFocus();

  const handleSubmit = async () => {
    if (!photo?.Id) return;
    setIsSubmitting(true);
    try {
      await PhotosService.editPhotoCaption(photo.Id, caption);

      // Update react-query cached photo using plainToClass to preserve getters like HasCaption
      // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
      queryClient.setQueriesData({ queryKey: ['photo', photo.Id] }, (old: Photo | undefined) => {
        if (!old) return old;
        return plainToClass(Photo, { ...old, Caption: caption });
      });
      if (photo.Guid) {
        queryClient.setQueriesData({ queryKey: ['photo', photo.Guid] }, (old: Photo | undefined) => {
          if (!old) return old;
          return plainToClass(Photo, { ...old, Caption: caption });
        });
      }

      showSuccessToast(t('photo.caption.updateSuccess'));
      onCaptionEdited?.(caption);
      onClose?.();
    } catch (error) {
      showErrorToast(t('photo.caption.updateError'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-3">
      <Textarea className="flex-1">
        <TextareaInput
          ref={inputRef}
          placeholder={t('photo.caption.placeholder')}
          value={caption}
          onChangeText={setCaption}
          textAlignVertical="top"
          className="m-0"
        />
      </Textarea>
      <HStack className="shrink-0 justify-end">
        <Button onPress={handleSubmit} disabled={isSubmitting} action="primary" size="md">
          <ButtonText>{t('common.update')}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}

EditPhotoCaption.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'md',
} satisfies ISheetConfig;

export default EditPhotoCaption;


